import EventEmitter from "eventemitter3";
import { OpusDecoder } from "opus-decoder";
import { AudioPlayer } from "./AudioPlayer";
import { VoiceBotOptions } from "./types";
import { AudioRecorder } from "./AudioRecorder";
import voiceProto from "./voice-bot.proto";
import { loadToken } from "../../util/token";
import { AUDIO_CONFIG, WS_CONFIG } from "./constants";

type VoiceChatEvent = "started" | "stopped" | "message" | "error";

export const VOICE_CHAT_EVENTS: Record<string, VoiceChatEvent> = {
    STARTED: "started",
    STOPPED: "stopped",
    MESSAGE: "message",
    ERROR: "error",
};

enum ConnectionState {
    DISCONNECTED = "disconnected",
    CONNECTING = "connecting",
    CONNECTED = "connected",
    STOPPING = "stopping",
}

export class AudioChat {
    private audioQueue: Array<Uint8Array> = [];
    private options: VoiceBotOptions;
    private endpoint: string;
    private bus: EventEmitter;
    private decoder: OpusDecoder;
    private ws?: WebSocket;
    private player?: AudioPlayer;
    private recorder?: AudioRecorder;
    private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 3;

    constructor(endpoint: string, options: VoiceBotOptions) {
        this.endpoint = endpoint;
        this.bus = new EventEmitter();
        this.decoder = new OpusDecoder({ forceStereo: true });
        this.options = options;
    }

    setOptions(options: VoiceBotOptions) {
        this.options = options;
        // console.info("Voice Bot Options updated:", options);
    }

    updateRealTimeConfig() {
        if (this.options.isRealtime && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const newConfig = {
                language: this.options.lang,
                tts_voice: this.options.voice,
                tts_speed: this.options.speed,
                tts_instructions: this.options.ttsInstructions,
                instructions: this.options.instructions,
                greeting: this.options.greeting,
                temperature: this.options.temperature,
            };

            const configRequest = voiceProto.voice.Request.create({
                config: newConfig,
            });

            this.ws.send(voiceProto.voice.Request.encode(configRequest).finish());

            console.info("New config sent to router:", newConfig);
        }
    }

    setEndpoint(endpoint: string) {
        this.endpoint = endpoint;
    }

    on(event: VoiceChatEvent, fn: (...args: any[]) => void): void {
        this.bus.on(event, fn);
    }

    start(): void {
        if (this.connectionState !== ConnectionState.DISCONNECTED) {
            console.warn("Cannot start: connection is not in disconnected state");
            return;
        }

        this.connectionState = ConnectionState.CONNECTING;
        this.connectWebSocket();
    }

    private connectWebSocket(): void {
        const voice = this.buildVoiceParam();
        const vad = this.buildVadParam();
        const wsUrl = `${this.endpoint}?lang=${this.options.lang}${voice}${vad}`;

        this.ws = new WebSocket(wsUrl);
        this.ws.onmessage = this.handleWebSocketMessage.bind(this);
        this.ws.onopen = this.handleWebSocketOpen.bind(this);
        this.ws.onclose = this.handleWebSocketClose.bind(this);
        this.ws.onerror = this.handleWebSocketError.bind(this);
    }

    private buildVoiceParam(): string {
        if (!this.options.voice) return "";

        let voice = `&voice=${this.options.voice}`;
        if (this.options.speed) {
            voice += `:${this.options.speed}`;
            if (this.options.style) {
                voice += `:${this.options.style}`;
            }
        }
        return voice;
    }

    private buildVadParam(): string {
        if (!this.options.vadThreshold) return "";

        return `&vad=${[
            this.options.vadThreshold,
            this.options.vadMinNonSpeech,
            this.options.vadMinUtterance,
            this.options.vadMaxUtterance,
        ].join(":")}`;
    }

    private handleWebSocketOpen(): void {
        if (!this.ws) return;

        this.connectionState = ConnectionState.CONNECTED;
        this.reconnectAttempts = 0;

        const { jwtToken } = loadToken();
        const authRequest = voiceProto.voice.Request.create({
            auth: { token: jwtToken },
        });
        this.ws.send(voiceProto.voice.Request.encode(authRequest).finish());

        setTimeout(() => {
            if (this.options.isRealtime) {
                this.updateRealTimeConfig();
            }
        }, WS_CONFIG.CONFIG_UPDATE_DELAY);

        this.recorder = new AudioRecorder((audioBlob) => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                const request = voiceProto.voice.Request.create({ audio: { audio: audioBlob } });
                this.ws.send(voiceProto.voice.Request.encode(request).finish());
            }
        });

        this.recorder.start();
        this.bus.emit(VOICE_CHAT_EVENTS.STARTED);
    }

    private handleWebSocketError(error: Event): void {
        console.error("WebSocket error", error);
        this.bus.emit(VOICE_CHAT_EVENTS.ERROR, error);
    }

    private handleWebSocketMessage(event: MessageEvent): void {
        if (!event.data) return;
        event.data
            .arrayBuffer()
            .then((buffer: any) => {
                const response = voiceProto.voice.Response.decode(new Uint8Array(buffer));

                if (response.transcript) {
                    this.bus.emit(VOICE_CHAT_EVENTS.MESSAGE, { type: "user", text: response.transcript.text });
                } else if (response.reply) {
                    this.bus.emit(VOICE_CHAT_EVENTS.MESSAGE, { type: "assistant", text: response.reply.text });
                } else if (response.audio) {
                    this.audioQueue.push(response.audio.audio);
                    this.playAudio();
                }
            })
            .catch((error: any) => {
                console.error("Error handling WebSocket message:", error);
                this.bus.emit(VOICE_CHAT_EVENTS.ERROR, error);
            });
    }

    stop(): void {
        if (
            this.connectionState === ConnectionState.STOPPING ||
            this.connectionState === ConnectionState.DISCONNECTED
        ) {
            return;
        }

        this.connectionState = ConnectionState.STOPPING;
        this.stopAudio();
        this.recorder?.stop();
        this.recorder = undefined;

        if (this.ws) {
            this.ws.close(WS_CONFIG.CLOSE_CODE_NORMAL, "Client closed connection");
        } else {
            this.handleDisconnection();
        }
    }

    private handleWebSocketClose(event: CloseEvent): void {
        console.info("WebSocket connection closed", event.code, event.reason);

        const wasConnected = this.connectionState === ConnectionState.CONNECTED;

        setTimeout(() => {
            this.handleDisconnection();

            // Auto-reconnect logic for unexpected disconnections
            if (
                wasConnected &&
                event.code !== WS_CONFIG.CLOSE_CODE_NORMAL &&
                this.reconnectAttempts < this.maxReconnectAttempts
            ) {
                this.reconnectAttempts++;
                console.info(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
                this.connectWebSocket();
            }
        }, WS_CONFIG.RECONNECT_DELAY);
    }

    private handleDisconnection(): void {
        this.ws = undefined;
        this.connectionState = ConnectionState.DISCONNECTED;
        this.bus.emit(VOICE_CHAT_EVENTS.STOPPED);
    }

    private playAudio(): void {
        if (this.audioQueue.length === 0) return;

        const opusFrame = this.audioQueue.shift()!;
        const { channelData, samplesDecoded, sampleRate } = this.decoder.decodeFrame(opusFrame);

        if (samplesDecoded > 0) {
            const pcmSamples = channelData[0];
            if (!this.player) {
                this.player = new AudioPlayer({
                    channels: AUDIO_CONFIG.DECODER_CHANNELS,
                    sampleRate,
                    flushingTime: AUDIO_CONFIG.PLAYER_FLUSHING_TIME,
                });
            }
            this.player.feed(pcmSamples);
        }
        this.playAudio();
    }

    private stopAudio(): void {
        if (this.player) {
            this.player.destroy();
            this.player = undefined;
            this.decoder.reset().catch(console.error);
        }
        this.audioQueue.length = 0;
    }
}
